/**
 * Einzelner Balken des Diagramms
 */
export interface Chart_Bucket {
	/** Achsen-Beschriftung (z. B. 'MO', 'KW 05', 'Jan') */
	label: string
	/** Summierter Wert (Minuten, Euro, …) */
	value: number
	/** Optionale Gruppe für Hintergrund-Bänder (z. B. Monatskürzel in der KW-Jahresansicht) */
	group?: string
}

/**
 * Optionen des Diagramms
 */
export interface Evaluation_Chart_Options {
	/** CSS-Selektor des SVG-Elements */
	selector: string
	/** Formatierung der Werte für Y-Achse und Ziel-Label */
	format_value?: ( value: number ) => string
	/** Optionaler Zielwert pro Bucket, gerendert als gestrichelte Linie */
	target?: number
	/** Beschriftung der Ziellinie (Standard: 'Ziel') */
	target_label?: string
	/** Nur jedes n-te X-Achsen-Label anzeigen (Standard: automatisch anhand der Bucket-Anzahl) */
	tick_step?: number

    /** Beschriftung der Y-Achse (z. B. 'Stunden', 'Euro') */
    y_label?: string
}

/**
 * Zusammenhängender Abschnitt gleicher Gruppe (für Hintergrund-Bänder)
 */
interface Group_Segment {
	group: string
	first: number
	last: number
}

/**
 * Generisches Balkendiagramm für die Auswertungen (Arbeitszeiten, Rechnungen).
 * Kennt keine Modi: es rendert beliebige Buckets (Wochentage, Kalenderwochen,
 * Monate) mit optionaler Ziellinie und Gruppen-Bändern. Welche Buckets
 * dargestellt werden, entscheidet der Aufrufer (siehe chart-buckets.ts).
 */
export class Evaluation_Chart {

	/** Standard-Innenabstände des Plots */
	private static readonly MARGIN = { top: 20, right: 20, bottom: 40, left: 60 }

	/** Rechter Rand, wenn ein Ziel-Label neben dem Plot gerendert wird */
	private static readonly TARGET_MARGIN_RIGHT = 120

	/** Übergebene Optionen */
	private readonly options: Evaluation_Chart_Options

	/** SVG-Element, in das gerendert wird */
	private readonly element: SVGSVGElement | null

    /** Mindesthöhe in Pixeln für Balken mit Wert > 0 */
    private static readonly MIN_BAR_HEIGHT = 5

    /** Tooltip-Element (wird beim ersten Zeigen erzeugt) */
    private tooltip: HTMLDivElement | null = null

	constructor( options: Evaluation_Chart_Options ) {
		this.options = options
		this.element = document.querySelector( options.selector )

		if ( ! this.element ) {
			console.warn( `Evaluation_Chart: kein SVG für Selektor "${options.selector}" gefunden` )
		}
	}

	/**
	 * Rendert die Buckets als Balkendiagramm und ersetzt dabei ein
	 * vorheriges Rendering. Über overrides lassen sich Optionen je Aufruf
	 * anpassen (z. B. unterschiedliche Zielwerte je Ansicht).
	 */
	public render( buckets: Chart_Bucket[], overrides: Partial<Evaluation_Chart_Options> = {} ): void {
		if ( ! this.element ) {
			return
		}

		const opts = { ...this.options, ...overrides }
		const format_value = opts.format_value ?? ( ( value: number ) => String( value ) )

		const svg = d3.select( this.element )
		svg.selectAll( '*' ).remove()

		const rect = this.element.getBoundingClientRect()
		const width = rect.width
		const height = rect.height

		const margin = { ...Evaluation_Chart.MARGIN }
		if ( opts.target !== undefined ) {
			margin.right = Evaluation_Chart.TARGET_MARGIN_RIGHT
		}

		// X-Achse über Indizes statt Labels, damit doppelte Labels unkritisch sind
		const x = d3
			.scaleBand()
			.domain( buckets.map( ( _, i ) => String( i ) ) )
			.range( [ margin.left, width - margin.right ] )
			.padding( 0.1 )

		const y_max = Math.max( d3.max( buckets, ( d ) => d.value ) || 0, opts.target ?? 0 )

		const y = d3
			.scaleLinear()
			.domain( [ 0, y_max ] )
			.nice()
			.range( [ height - margin.bottom, margin.top ] )

		// ClipPath, damit Balken nicht in den Rand laufen (ID je Element eindeutig)
		const clip_id = `plot-clip-${this.element.id || 'chart'}`

		svg
			.append( 'clipPath' )
			.attr( 'id', clip_id )
			.append( 'rect' )
			.attr( 'x', margin.left )
			.attr( 'y', margin.top )
			.attr( 'width', width - margin.left - margin.right )
			.attr( 'height', height - margin.top - margin.bottom )

		// Layer
		const g_groups = svg.append( 'g' ).attr( 'class', 'group-bands' ).attr( 'clip-path', `url(#${clip_id})` )
		const g_bars = svg.append( 'g' ).attr( 'class', 'bars' ).attr( 'clip-path', `url(#${clip_id})` )
		const g_guides = svg.append( 'g' ).attr( 'class', 'guides' )
		const g_axes = svg.append( 'g' ).attr( 'class', 'axes' )

		this.render_group_bands( g_groups, buckets, x, margin.top, height - margin.top - margin.bottom )
		this.render_bars( g_bars, buckets, x, y, opts.target )
		this.render_axes( g_axes, buckets, x, y, opts, format_value, height, margin.left )

		if ( opts.target !== undefined ) {
			this.render_target( g_guides, y, opts.target, opts.target_label ?? 'Ziel', format_value, margin, width )
		}

		g_guides.raise()

        const g_hover = svg.append( 'g' ).attr( 'class', 'hover-targets' )
        this.render_hover_targets( g_hover, buckets, x, y, format_value, margin, height )
	}

	/**
	 * Entfernt das aktuelle Rendering (z. B. beim Umschalten der Ansicht)
	 */
	public clear(): void {
		if ( ! this.element ) {
			return
		}

		d3.select( this.element ).selectAll( '*' ).remove()
	}

	/**
	 * Liefert den data-status eines Balkens für SCSS-Styling
	 */
	private get_status( value: number, target?: number ): string {
		if ( target === undefined ) {
			return 'neutral'
		}

		return value >= target ? 'reached' : 'below'
	}

	/**
	 * Fasst aufeinanderfolgende Buckets gleicher Gruppe zu Segmenten zusammen
	 */
	private get_group_segments( buckets: Chart_Bucket[] ): Group_Segment[] {
		const segments: Group_Segment[] = []

		buckets.forEach( ( bucket, i ) => {
			if ( bucket.group === undefined ) {
				return
			}

			const previous = segments[ segments.length - 1 ]

			if ( previous && previous.group === bucket.group && previous.last === i - 1 ) {
				previous.last = i
			} else {
				segments.push( { group: bucket.group, first: i, last: i } )
			}
		} )

		return segments
	}

	/**
	 * Rendert alternierend hinterlegte Bänder samt Gruppen-Label
	 */
	private render_group_bands( g: d3.Selection<SVGGElement, unknown, null, undefined>, buckets: Chart_Bucket[], x: d3.ScaleBand<string>, top: number, band_height: number ): void {
		const segments = this.get_group_segments( buckets )

		const x_start = ( segment: Group_Segment ): number => x( String( segment.first ) ) as number
		const x_end = ( segment: Group_Segment ): number => ( x( String( segment.last ) ) as number ) + x.bandwidth()

		g
			.selectAll( 'rect' )
			.data( segments )
			.join( 'rect' )
			.attr( 'x', ( d ) => x_start( d ) )
			.attr( 'y', top )
			.attr( 'width', ( d ) => x_end( d ) - x_start( d ) )
			.attr( 'height', band_height )
			.attr( 'fill', 'currentColor' )
			.attr( 'opacity', ( _, i ) => i % 2 === 0 ? 0.06 : 0 )

		g
			.selectAll( 'text' )
			.data( segments )
			.join( 'text' )
			.attr( 'x', ( d ) => ( x_start( d ) + x_end( d ) ) / 2 )
			.attr( 'y', top + 10 )
			.attr( 'text-anchor', 'middle' )
			.attr( 'font-size', 10 )
			.attr( 'opacity', 0.5 )
			.text( ( d ) => d.group )
	}

    /**
     * Rendert die Balken; data-status ermöglicht Styling per SCSS.
     * Werte über 0 werden mindestens MIN_BAR_HEIGHT hoch gerendert,
     * damit Kleinstwerte sichtbar bleiben.
     */
    private render_bars( g: d3.Selection<SVGGElement, unknown, null, undefined>, buckets: Chart_Bucket[], x: d3.ScaleBand<string>, y: d3.ScaleLinear<number, number>, target?: number ): void {
        const bar_height = ( value: number ): number => {
            if ( value <= 0 ) {
                return 0
            }

            return Math.max( y( 0 ) - y( value ), Evaluation_Chart.MIN_BAR_HEIGHT )
        }

        g
            .selectAll( 'rect' )
            .data( buckets )
            .join( 'rect' )
            .attr( 'x', ( _, i ) => x( String( i ) ) as number )
            .attr( 'y', ( d ) => y( 0 ) - bar_height( d.value ) )
            .attr( 'width', x.bandwidth() )
            .attr( 'height', ( d ) => bar_height( d.value ) )
            .attr( 'data-status', ( d ) => this.get_status( d.value, target ) )
    }

	/**
	 * Rendert X- und Y-Achse; die X-Achse zeigt je nach tick_step nur
	 * jedes n-te Label (Standard: maximal ca. 26 Labels)
	 */
	private render_axes( g: d3.Selection<SVGGElement, unknown, null, undefined>, buckets: Chart_Bucket[], x: d3.ScaleBand<string>, y: d3.ScaleLinear<number, number>, opts: Evaluation_Chart_Options, format_value: ( value: number ) => string, height: number, margin_left: number ): void {
		const tick_step = opts.tick_step ?? Math.max( 1, Math.ceil( buckets.length / 26 ) )

		g
			.append( 'g' )
			.attr( 'transform', `translate(0,${height - Evaluation_Chart.MARGIN.bottom})` )
			.call(
				d3
					.axisBottom( x )
					.tickValues( x.domain().filter( ( _, i ) => i % tick_step === 0 ) )
					.tickFormat( ( key ) => buckets[ Number( key ) ].label )
			)
			.selectAll( 'text' )
			.style( 'text-anchor', 'middle' )

		g
			.append( 'g' )
			.attr( 'transform', `translate(${margin_left},0)` )
			.call(
				d3
					.axisLeft( y )
					.ticks( 6 )
					.tickFormat( ( v ) => format_value( v as number ) )
			)

        if ( opts.y_label ) {
            g
                .append( 'text' )
                .attr( 'x', margin_left )
                .attr( 'y', Evaluation_Chart.MARGIN.top - 8 )
                .attr( 'text-anchor', 'end' )
                .attr( 'font-size', 10 )
                .attr( 'opacity', 0.6 )
                .text( opts.y_label )
        }
	}

	/**
	 * Rendert die gestrichelte Ziellinie samt Label rechts neben dem Plot
	 */
	private render_target( g: d3.Selection<SVGGElement, unknown, null, undefined>, y: d3.ScaleLinear<number, number>, target: number, target_label: string, format_value: ( value: number ) => string, margin: { top: number, right: number, bottom: number, left: number }, width: number ): void {
		const y_target = y( target )

		g
			.append( 'line' )
			.attr( 'x1', margin.left )
			.attr( 'x2', width - margin.right )
			.attr( 'y1', y_target )
			.attr( 'y2', y_target )
			.attr( 'stroke', 'currentColor' )
			.attr( 'stroke-width', 1.5 )
			.attr( 'stroke-dasharray', '6,6' )

		g
			.append( 'text' )
			.attr( 'x', width - margin.right + 8 )
			.attr( 'y', y_target - 6 )
			.attr( 'text-anchor', 'start' )
			.attr( 'font-size', 12 )
			.attr( 'font-weight', 600 )
			.text( `${target_label}: ${format_value( target )}` )
	}

    /**
     * Liefert das Tooltip-Element und erzeugt es beim ersten Aufruf.
     * Ein Tap außerhalb des Charts blendet es aus (Touch-Bedienung).
     */
    private get_tooltip(): HTMLDivElement {
        if ( ! this.tooltip ) {
            this.tooltip = document.createElement( 'div' )
            this.tooltip.className = 'chart-tooltip'
            this.tooltip.style.position = 'fixed'
            this.tooltip.style.pointerEvents = 'none'
            this.tooltip.style.display = 'none'
            document.body.appendChild( this.tooltip )

            document.addEventListener( 'pointerdown', ( event ) => {
                if ( this.element && ! this.element.contains( event.target as Node ) ) {
                    this.hide_tooltip()
                }
            } )
        }

        return this.tooltip
    }

    /**
     * Zeigt das Tooltip zentriert über der angegebenen Position (Chart-Koordinaten)
     */
    private show_tooltip( content: string, x_center: number, y_top: number ): void {
        if ( ! this.element ) {
            return
        }

        const rect = this.element.getBoundingClientRect()
        const tooltip = this.get_tooltip()

        tooltip.textContent = content
        tooltip.style.left = `${rect.left + x_center}px`
        tooltip.style.top = `${rect.top + y_top}px`
        tooltip.style.transform = 'translate(-50%, -100%)'
        tooltip.style.display = 'block'
    }

    /**
     * Blendet das Tooltip aus
     */
    private hide_tooltip(): void {
        if ( this.tooltip ) {
            this.tooltip.style.display = 'none'
        }
    }

    /**
     * Rendert unsichtbare Trefferflächen über die volle Spaltenhöhe.
     * Maus: Tooltip bei Hover. Touch: Tap zeigt das Tooltip dauerhaft,
     * ausgeblendet wird per Tap außerhalb des Charts.
     */
    private render_hover_targets( g: d3.Selection<SVGGElement, unknown, null, undefined>, buckets: Chart_Bucket[], x: d3.ScaleBand<string>, y: d3.ScaleLinear<number, number>, format_value: ( value: number ) => string, margin: { top: number, right: number, bottom: number, left: number }, height: number ): void {
        g
            .selectAll( 'rect' )
            .data( buckets )
            .join( 'rect' )
            .attr( 'x', ( _, i ) => x( String( i ) ) as number )
            .attr( 'y', margin.top )
            .attr( 'width', x.bandwidth() )
            .attr( 'height', height - margin.top - margin.bottom )
            .attr( 'fill', 'transparent' )
            .on( 'pointerenter', ( _, d ) => {
                const i = buckets.indexOf( d )
                const x_center = ( x( String( i ) ) as number ) + x.bandwidth() / 2

                this.show_tooltip( `${d.label}: ${format_value( d.value )}`, x_center, y( d.value ) )
            } )
            .on( 'pointerleave', ( event: PointerEvent ) => {
                if ( event.pointerType === 'mouse' ) {
                    this.hide_tooltip()
                }
            } )
    }
}
